import { GridLayoutItem, WidgetTarget } from '@/dashboard/shared/types/widgets.types';
import { TargetTag } from '../../globalVariables/domain/globalVariable.types';
import {
  GLOBAL_VARIABLE_TAG_KEY,
  TREE_GLOBAL_VARIABLE_TAG_KEY,
} from '@/common/define/widget.define';
import { CUSTOM_STAT_PREFIX } from '@/common/utils/statInfo.define';

/**
 * 초기화된 타겟을 반환한다.
 * targetes의 tagKey가 'GlobalVariable'인 경우, globalFilterIds에 있으면 그대로 반환
 * 'Tree Global Variable'인 경우 treeFilterIds에 있으면 그대로 반환
 * 기존의 targets가 있다면 기존 target의 category로 와일드카드 타겟을 추가한다.
 * 글로벌 변수만 있는 경우 지표 category를 기준으로 와일드카드 타겟을 추가한다.
 * 지표 category가 ''인 경우 빈 배열을 반환한다.
 * targets가 빈 배열이면 빈 배열을 반환한다.
 *
 * @param targets - 초기화할 타겟 리스트
 * @param statCategory - 지표의 category
 * @param globalFilterIds - 글로벌 변수 ID 리스트
 * @param treeFilterIds - 트리 글로벌 변수 ID 리스트
 * @returns 초기화된 타겟 리스트
 */
const getInitializeTargets = ({
  targets,
  statCategory,
  globalFilterIds,
  treeFilterIds,
}: {
  targets: WidgetTarget[];
  statCategory: string;
  globalFilterIds: Set<number>;
  treeFilterIds: Set<number>;
}): WidgetTarget[] => {
  if (targets.length === 0) return [];

  const hasGlobalVariable = targets.some(
    ({ tagKey }) => tagKey === GLOBAL_VARIABLE_TAG_KEY || tagKey === TREE_GLOBAL_VARIABLE_TAG_KEY,
  );

  if (!hasGlobalVariable) {
    const tagKeyMap = new Map<string, WidgetTarget>();
    targets.forEach((target) => {
      if (target.tagKey && !tagKeyMap.has(target.tagKey)) {
        tagKeyMap.set(target.tagKey, { ...target, tagValue: '*', tagValueId: '*' });
      }
    });
    return Array.from(tagKeyMap.values());
  }

  const targetMap = new Map<string, WidgetTarget>();
  targets.forEach((target) => {
    const tagValue = Number(target.tagValue);

    if (target.tagKey === GLOBAL_VARIABLE_TAG_KEY) {
      if (globalFilterIds.has(tagValue)) {
        targetMap.set(target.tagKey, target);
      }
    } else if (target.tagKey === TREE_GLOBAL_VARIABLE_TAG_KEY) {
      if (treeFilterIds.has(tagValue)) {
        targetMap.set(target.tagKey, target);
      }
    } else if (target.tagKey && !targetMap.has(target.tagKey)) {
      targetMap.set(target.tagKey, { ...target, tagValue: '*', tagValueId: '*' });
    }
  });

  const newTargets = Array.from(targetMap.values());

  if (!newTargets.length && statCategory !== '') {
    return [{ tagKey: statCategory, tagType: 'tag', tagValue: '*', tagValueId: '*' }];
  }

  return newTargets;
};

/**
 * 초기화된 타겟 태그를 반환한다.
 * targetTags의 groupTagId가 'GlobalVariable'인 경우, globalFilterIds에 있으면 그대로 반환
 * 'Tree Global Variable'인 경우 treeFilterIds에 있으면 그대로 반환
 * 기존의 targetTags가 있다면 기존 target의 category로 와일드카드 타겟을 추가한다.
 * 글로벌 변수만 있는 경우 지표 category를 기준으로 와일드카드 타겟을 추가한다.
 * 지표 category가 ''인 경우 빈 배열을 반환한다.
 * targetTags가 빈 배열이면 빈 배열을 반환한다.
 *
 * @param targetTags - 초기화할 타겟 태그 리스트
 * @param statCategory - 지표의 category
 * @param globalFilterIds - 글로벌 변수 ID 리스트
 * @param treeFilterIds - 트리 글로벌 변수 ID 리스트
 * @returns 초기화된 타겟 리스트
 */
const getInitializeTargetTags = ({
  targetTags,
  statCategory,
  globalFilterIds,
  treeFilterIds,
}: {
  targetTags: TargetTag[];
  statCategory: string;
  globalFilterIds: Set<number>;
  treeFilterIds: Set<number>;
}): TargetTag[] => {
  if (targetTags.length === 0) return [];

  const hasGlobalVariable = targetTags.some(
    ({ groupTagId }) =>
      groupTagId === GLOBAL_VARIABLE_TAG_KEY || groupTagId === TREE_GLOBAL_VARIABLE_TAG_KEY,
  );

  if (!hasGlobalVariable) {
    const groupTagIdMap = new Map<string, TargetTag>();
    targetTags.forEach((tag) => {
      if (tag.groupTagId && !groupTagIdMap.has(tag.groupTagId)) {
        groupTagIdMap.set(tag.groupTagId, { ...tag, tagId: '*', tagType: 'tag' });
      }
    });
    return Array.from(groupTagIdMap.values());
  }

  const targetTagMap = new Map<string, TargetTag>();
  targetTags.forEach((tag) => {
    const tagId = Number(tag.tagId);

    if (tag.groupTagId === GLOBAL_VARIABLE_TAG_KEY) {
      if (globalFilterIds.has(tagId)) {
        targetTagMap.set(tag.groupTagId, tag);
      }
    } else if (tag.groupTagId === TREE_GLOBAL_VARIABLE_TAG_KEY) {
      if (treeFilterIds.has(tagId)) {
        targetTagMap.set(tag.groupTagId, tag);
      }
    } else if (tag.groupTagId && !targetTagMap.has(tag.groupTagId)) {
      targetTagMap.set(tag.groupTagId, { ...tag, tagId: '*', tagType: 'tag' });
    }
  });

  const newTargetTags = [...targetTagMap.values()];

  if (!newTargetTags.length && statCategory !== '') {
    return [{ groupTagId: statCategory, tagId: '*', tagType: 'tag' }];
  }

  return newTargetTags;
};

const getAdjustedCategory = (category: string): string => {
  if (category.toLowerCase().includes('intermax')) return 'intermax';
  if (category.toLowerCase().includes('maxgauge')) return 'maxgauge';
  if (category.startsWith(CUSTOM_STAT_PREFIX)) return '';
  if (category === 'target') return '';
  return category;
};

/**
 * 차트 데이터의 targets를 초기화한 새 item을 반환합니다. (immutable)
 * children은 getNewGridLayoutItem의 재귀 호출로 처리됩니다.
 * referenceInfo가 있으면 createType을 'base'로 설정하고 referenceInfo를 제거합니다.
 *
 * @param item - 초기화할 item
 * @param globalFilterIds - 글로벌 변수 ID 리스트
 * @param treeFilterIds - 트리 글로벌 변수 ID 리스트
 * @returns 초기화된 새 item
 */
export const initializeTargets = (
  item: GridLayoutItem,
  globalFilterIds: Set<number>,
  treeFilterIds: Set<number>,
): GridLayoutItem => {
  try {
    const { chartType, displayStyle } = item;

    const referenceInfo = item.chartOption?.referenceInfo;

    const chartData = item.chartData?.map((cd) => {
      let newChartData = { ...cd };
      const adjustedCategory = getAdjustedCategory(cd.category);

      if (chartType === 'CLUSTER' && cd.value?.tags) {
        const newTags = Object.entries(cd.value.tags).reduce<Record<string, object[]>>(
          (acc, [key, tagInfo]) => {
            acc[key] = getInitializeTargetTags({
              targetTags: tagInfo as TargetTag[],
              statCategory: key,
              globalFilterIds,
              treeFilterIds,
            });
            return acc;
          },
          {},
        );
        newChartData = { ...newChartData, value: { ...newChartData.value, tags: newTags } };
      }

      return {
        ...newChartData,
        targets: referenceInfo
          ? []
          : getInitializeTargets({
              targets: cd.targets,
              statCategory: adjustedCategory,
              globalFilterIds,
              treeFilterIds,
            }),
      };
    });

    let chartOption = item.chartOption;
    if (chartType === 'TOPOLOGY') {
      const tagKey = getAdjustedCategory(item.chartData?.[0]?.category ?? '');
      if (tagKey) {
        chartOption = {
          ...chartOption,
          nodeOptions: chartOption.nodeOptions?.map((nodeOption) => {
            return {
              ...nodeOption,
              targets: getInitializeTargetTags({
                targetTags: nodeOption.targets,
                statCategory: tagKey,
                globalFilterIds,
                treeFilterIds,
              }),
            };
          }),
        };
      }
    } else if (chartType === 'TIME_SERIES' && displayStyle === 'AREA') {
      const markerByMetrics = chartOption.markerByMetrics;
      if (markerByMetrics?.targets && markerByMetrics?.category) {
        chartOption = {
          ...chartOption,
          markerByMetrics: {
            ...markerByMetrics,
            targets: getInitializeTargets({
              targets: markerByMetrics.targets,
              statCategory: getAdjustedCategory(markerByMetrics.category),
              globalFilterIds,
              treeFilterIds,
            }),
          },
        };
      }
    }

    if (referenceInfo) {
      chartOption = {
        ...chartOption,
        referenceInfo: undefined,
      };
    }

    return {
      ...item,
      chartData,
      chartOption,
    };
  } catch (error) {
    console.error('initializeTargets error', error);
    return item;
  }
};
